import { Link } from 'react-router-dom';
import {
  EGB_AI_ID,
  COUNCIL_ROLES,
  COUNCIL_FUNCTIONS,
  AI_PRINCIPLES,
  LIFECYCLE_STAGES,
  MODEL_GOVERNANCE,
  PROMPT_GOVERNANCE,
  DNA_LAYERS,
  AGENT_GOVERNANCE,
  DECISION_REQUIREMENTS,
  RESPONSIBLE_AI,
  AI_RISKS,
  HUMAN_OVERSIGHT,
  AI_COST,
  AI_SECURITY,
  AI_COMPLIANCE,
  AI_AUDIT,
  CONTINUOUS_LEARNING,
  AI_METRICS,
  AI_ETHICS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_COUNCIL,
  DEMO_PRINCIPLES,
  DEMO_LIFECYCLE,
  DEMO_PROMPT_DNA,
  DEMO_AGENT_DECISION,
  DEMO_RISK,
  DEMO_OVERSIGHT,
  DEMO_COMPLIANCE,
  buildAIGovernanceDashboard,
} from '../../enterpriseaigovernance/framework';
import {
  AIGovernanceCouncilPanel,
  AIPrinciplesPanel,
  AILifecyclePanel,
  PromptDNAPanel,
  AIAgentDecisionPanel,
  ResponsibleAIRiskPanel,
  HumanOversightSecurityPanel,
  AIComplianceMetricsPanel,
} from '../../components/enterpriseaigovernance';
import { Card, KpiCard } from '../../components/ui';

export function EnterpriseAIGovernancePage() {
  const dashboard = buildAIGovernanceDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EGB_AI_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise AI Governance</h1>
        <p className="mt-1 text-sm text-slate-500">
          Permanent AI Constitution — responsible AI, risk management, and intelligence lifecycle governance.
          Fully aligned with MPC-00001, EGB-00001, and EGB-00002.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-100 dark:border-purple-900 dark:bg-purple-950 dark:text-purple-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        <KpiCard label="Council Roles" value={dashboard.council_roles.toString()} />
        <KpiCard label="AI Principles" value={dashboard.ai_principles.toString()} />
        <KpiCard label="Lifecycle Stages" value={dashboard.lifecycle_stages.toString()} />
        <KpiCard label="DNA Layers" value={dashboard.dna_layers.toString()} />
        <KpiCard label="Risk Categories" value={dashboard.risk_categories.toString()} />
        <KpiCard label="AI Metrics" value={dashboard.ai_metrics.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">AI Governance Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Governance Council</h2>
        <p className="mt-1 text-sm text-slate-500">{COUNCIL_ROLES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{COUNCIL_FUNCTIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIGovernanceCouncilPanel members={DEMO_COUNCIL} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Principles</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_PRINCIPLES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIPrinciplesPanel principles={DEMO_PRINCIPLES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Lifecycle & Model Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{LIFECYCLE_STAGES.join(' · ')} · {MODEL_GOVERNANCE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AILifecyclePanel pipeline={DEMO_LIFECYCLE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Prompt & DNA Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{PROMPT_GOVERNANCE.join(' · ')} · {DNA_LAYERS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <PromptDNAPanel items={DEMO_PROMPT_DNA} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Agent & Decision Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{AGENT_GOVERNANCE.join(' · ')} · {DECISION_REQUIREMENTS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIAgentDecisionPanel items={DEMO_AGENT_DECISION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Responsible AI & Risk Management</h2>
        <p className="mt-1 text-sm text-slate-500">{RESPONSIBLE_AI.join(' · ')} · {AI_RISKS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ResponsibleAIRiskPanel items={DEMO_RISK} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Human Oversight, Cost & Security</h2>
        <p className="mt-1 text-sm text-slate-500">{HUMAN_OVERSIGHT.join(' · ')} · {AI_COST.join(' · ')} · {AI_SECURITY.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <HumanOversightSecurityPanel items={DEMO_OVERSIGHT} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Compliance, Audit, Metrics & Ethics</h2>
        <p className="mt-1 text-sm text-slate-500">
          {AI_COMPLIANCE.join(' · ')} · {AI_AUDIT.join(' · ')} · {CONTINUOUS_LEARNING.join(' · ')} · {AI_METRICS.join(' · ')} · {AI_ETHICS.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <AIComplianceMetricsPanel items={DEMO_COMPLIANCE} />
        </Card>
      </section>
    </div>
  );
}
